import { Box, Typography } from '@mui/material';
import { BikeleasingV2ConditionDTO, BikeleasingV2EmployerContributionDTOTypeEnum } from 'probonio-shared-ui/api';
import { useTranslation } from 'react-i18next';

const ConditionField: React.FC<{ label: string; value: string; dataTestId?: string }> = ({ label, value, dataTestId }) => {
  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        flexGrow: 1,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          mr: 5,
          color: 'text.secondary',
          fontWeight: 'bold',
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        data-test-id={dataTestId}
        sx={{
          fontWeight: 'bold',
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

export const SelectedBikeleasingCondition: React.FC<{ bikeleasingCondition?: BikeleasingV2ConditionDTO | null }> = ({
  bikeleasingCondition,
}) => {
  const { t } = useTranslation('benefitsModule');
  if (!bikeleasingCondition) {
    return;
  }
  return (
    <>
      {bikeleasingCondition.companyCode && (
        <Typography
          variant="h3"
          sx={{
            color: 'text.secondary',
            mb: 1,
          }}
        >
          {t('extensions:bikeleasing_v2.companyCode', { companyCode: bikeleasingCondition.companyCode })}
        </Typography>
      )}
      {bikeleasingCondition.maxNumberOfBikesPerEmployee && (
        <ConditionField
          value={`${bikeleasingCondition.maxNumberOfBikesPerEmployee}`}
          label={t('extensions:bikeleasing_v2.maxNumberOfBikesPerEmployee')}
        />
      )}
      {bikeleasingCondition.leasingDurationInMonths && (
        <ConditionField
          value={`${bikeleasingCondition.leasingDurationInMonths}`}
          label={t('extensions:bikeleasing_v2.leasingDurationInMonths')}
        />
      )}
      {bikeleasingCondition.allowedBikeTypes?.length && (
        <ConditionField value={bikeleasingCondition.allowedBikeTypes.join(', ')} label={t('extensions:bikeleasing_v2.allowedBikeTypes')} />
      )}
      {bikeleasingCondition.priceRestrictions?.minPurchasePrice && (
        <ConditionField
          value={t('common:money', { money: bikeleasingCondition.priceRestrictions?.minPurchasePrice.grossAmount })}
          label={t('extensions:bikeleasing_v2.minPurchasePrice')}
        />
      )}
      {bikeleasingCondition.priceRestrictions?.maxPurchasePrice && (
        <ConditionField
          value={t('common:money', { money: bikeleasingCondition.priceRestrictions?.maxPurchasePrice.grossAmount })}
          label={t('extensions:bikeleasing_v2.maxPurchasePrice')}
        />
      )}
      <ConditionField
        value={
          bikeleasingCondition.priceRestrictions?.maxTotalPurchasePriceForAllBikes
            ? t('common:money', { money: bikeleasingCondition.priceRestrictions?.maxTotalPurchasePriceForAllBikes.grossAmount })
            : '-'
        }
        label={t('extensions:bikeleasing_v2.priceRestrictions.maxTotalPurchasePriceForAllBikes')}
      />
      {bikeleasingCondition.employerContribution?.type === BikeleasingV2EmployerContributionDTOTypeEnum.Absolute && (
        <ConditionField
          value={t('common:money', { money: bikeleasingCondition.employerContribution.amount })}
          label={t('extensions:bikeleasing_v2.employerContribution')}
        />
      )}
      {bikeleasingCondition.employerContribution?.type === BikeleasingV2EmployerContributionDTOTypeEnum.Percentage && (
        <ConditionField
          value={`${bikeleasingCondition.employerContribution.percentage}`}
          label={t('extensions:bikeleasing_v2.employerContribution')}
        />
      )}
    </>
  );
};
